import "@/styles/globals.css";
import "@/styles/ai-animations.css";
import "@/styles/modern-theme.css";
import type { AppProps } from "next/app";
import { AppLayout } from "@/layouts/app-layout";
import type { NextPageWithLayout } from "@/layouts/page-layout";
import { useEffect } from "react";
import { RecoilRoot } from "recoil";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import en from "@/languages/en.json";
import he from "@/languages/he.json";
import ar from "@/languages/ar.json";
import de from "@/languages/de.json";
import { ThemeProvider } from "@/providers";
import { GomakeLoading } from "@/widgets";
import { GoMakeSnackBar } from "@/components";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Backend from "i18next-http-backend";
import { GoMakeTourProvider } from "@/providers/go-make-tour/go-make-tour-provider";
import { GomakeLoadingWithShadow } from "@/widgets/loading-with-shadow";
import useUpdateCurrentPath from "@/pages-components/customer-service/hook/useUpdateCurrentPath";
import EnhancedErrorBoundary from "@/components/jira-logger/ErrorBoundary";
import "rsuite/dist/rsuite-no-reset.min.css";
import { FileUploaderProvider } from "@/providers/signalr-provider/file-uploader-provider";
import { DateFormatProvider } from "@/contexts/date-format-provider";
import { useRouter } from "next/router";
import { MultiThemeProvider } from "@/contexts/theme";
import { ModernAntdCompat } from "@/themes/modern/components/modern-antd-compat";
import { DynamicFavicon } from "@/components/dynamic-favicon/dynamic-favicon";
import Head from "next/head";
import { useDocumentReportReadySocket } from "@/hooks/socket-io/use-document-report-ready-socket";
import { InternalMessageToastHost } from "@/widgets/frontdesk-notifications/components/internal-message-toast-host";
import { InternalChatPopup } from "@/widgets/frontdesk-notifications/components/internal-chat-popup";

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: `https://gomake-translations.s3.eu-central-1.amazonaws.com/web/{{lng}}.json`,
    },
    lng: "en",
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const { t } = useTranslation();
  const router = useRouter();
  const documentDir = t("direction") === "rtl" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.setAttribute("dir", documentDir);
  }, [documentDir]);

  /** Deep-merge repo JSON into i18n so new keys work before S3 `web/{{lng}}.json` is updated. */
  useEffect(() => {
    const bundles: Array<[string, object]> = [
      ["en", en]
    ];
    const mergeLocal = () => {
      bundles.forEach(([lng, data]) => {
        i18n.addResourceBundle(lng, "translation", data, true, false);
      });
    };
    mergeLocal();
    i18n.on("languageChanged", mergeLocal);
    const w = typeof window !== "undefined" ? window : undefined;
    const afterRemote = w?.setTimeout(mergeLocal, 800);
    const afterRemoteLate = w?.setTimeout(mergeLocal, 2500);
    return () => {
      if (afterRemote !== undefined) w?.clearTimeout(afterRemote);
      if (afterRemoteLate !== undefined) w?.clearTimeout(afterRemoteLate);
      i18n.off("languageChanged", mergeLocal);
    };
  }, []);

  return (
    <DndProvider backend={HTML5Backend}>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
      </Head>
      <RecoilRoot>
        <FileUploaderProvider>
            <UpdatePathWrapper>
              <EnhancedErrorBoundary>
                <MultiThemeProvider>
                  <ThemeProvider />
                  <ModernAntdCompat>
                  <DateFormatProvider>
                    <style jsx global>{`
                      html {
                        direction: ${documentDir};
                      }
                    `}</style>
                    <DynamicFavicon />
                    <GoMakeTourProvider>
                      <AppLayout Component={Component}>
                        <Component {...pageProps} />
                      </AppLayout>
                    </GoMakeTourProvider>
                    <GoMakeSnackBar />
                    <InternalMessageToastHost />
                    <InternalChatPopup />
                    <GomakeLoading />
                    <GomakeLoadingWithShadow />
                  </DateFormatProvider>
                  </ModernAntdCompat>
                </MultiThemeProvider>
              </EnhancedErrorBoundary>
            </UpdatePathWrapper>
        </FileUploaderProvider>
      </RecoilRoot>
    </DndProvider>
  );
}

const UpdatePathWrapper = ({ children }) => {
  useUpdateCurrentPath();
  useDocumentReportReadySocket();
  return children;
};
